import {
  DetailsPageDescription,
  DetailsPageHeader,
  FormattedLink,
  PageContext,
  RoutingData,
  SpaTermsAndConditions,
  Spinner,
} from '@common/components';
import { BasketDrawer } from '@common/components/BasketDrawer/BasketDrawer';
import { calculateTotalNumberOfItemsInBasket } from '@common/components/BasketDrawer/BasketDrawer.helpers';
import { ModuleType } from '@common/components/BasketDrawer/BasketListing.types';
import { useBasketDrawerCheckoutCta } from '@common/components/BasketDrawer/hooks/useBasketDrawerCheckoutCta';
import { useModulesToRecommend } from '@common/components/BasketDrawer/hooks/useModulesToRecommend';
import Markdown from '@common/components/Markdown';
import SectionContainer from '@common/components/SectionContainer';
import Suggestions from '@common/components/Suggestions';
import { Routes } from '@common/constants';
import { useLabels } from '@features/Menu/useLabels';
import { useGetSpaPackageTimeslots } from '@features/spa/hooks/useGetSpaPackageTimeslots';
import { useGetSpaPackage, useGetSpaSettings } from '@features/spa/hooks';
import { useTranslations } from '@lang';
import { ArrowLeftIcon } from '@root/styles/icons';
import { basketSelectors } from '@store/basket/selectors';
import { yourJourneySelectors } from '@store/yourJourney/selectors';
import { mergeCrossSellWithUpsell } from '@utils/helpers';
import moment from 'moment';
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'shared-routing';
import { BasketDetails } from 'shared-types/Basket';
import styled from 'styled-components';
import { SpaDayProps } from './SpaDay.types';
import { SpaPackageModal } from './SpaPackageModal/SpaPackageModal';
import { SpaStickyBar } from './StickyBar';

const SpaDayWrapper = styled.div`
  margin-top: 10px;
`;

const STICKY_BAR_ID = 'spa-treatments-sticky-bar';

export const SpaDay: FC<SpaDayProps> = ({ bookingStep, property, language, id }) => {
  const journeyParams = useSelector(yourJourneySelectors.getParsedJourneyParams);
  const basketDetails = useSelector(basketSelectors.getBasketDetails) as BasketDetails | null;
  const selectedDate = moment(journeyParams?.spaDate);

  const startDate = moment(journeyParams?.spaDate).format('YYYY-MM-DD');

  const { data: spaPackage } = useGetSpaPackage(property.id, id, language);

  const { data: timeslots, isFetching: isFetchingTimeslots } = useGetSpaPackageTimeslots(
    property.id,
    id,
    startDate,
    spaPackage?.numberOfGuests
  );
  const { data: spaSettings } = useGetSpaSettings(property.id);
  const { termsAndConditions = '' } = spaSettings || {};
  const router = useRouter();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [isMinibasketVisible, setIsMinibasketVisible] = useState(false);

  const { t } = useTranslations();

  const labels = useLabels();

  const modules = Object.keys(property.modulesExcludingDisabled);

  const checkoutCta = useBasketDrawerCheckoutCta();

  const modulesToRecommend = useModulesToRecommend({
    modules: modules as ModuleType[],
    items: basketDetails?.items ?? [],
  });

  useEffect(() => {
    if (bookingStep) {
      setIsModalOpen(true);
    }
  }, [bookingStep]);

  const routingData: RoutingData = {
    message: t('common.backToModuleRoot', {
      entity: labels?.spadays || t('app.spaDays'),
    }),
    href: Routes.SPA_DAY_LIST,
  };
  const parentPageName = t('spa.spaDay');

  if (!spaPackage || isFetchingTimeslots) {
    return <Spinner showDelayMessage isActive={isFetchingTimeslots} />;
  }

  const closeModal = (showMiniBasket = false) => {
    setIsModalOpen(false);
    router.push({
      pathname: Routes.SPA_DAY_DETAIL,
      query: {
        id: spaPackage.id,
      },
    });
    if (showMiniBasket) {
      setIsMinibasketVisible(true);
    }
  };

  const mergedItems = mergeCrossSellWithUpsell(
    spaPackage.upSell,
    'spa-day',
    spaPackage.crossSellObjects
  );

  const selectedDateTimeSlots = timeslots?.[selectedDate.format('YYYY-MM-DD')];

  const discount = Object.values(selectedDateTimeSlots ?? {})
    .flat()
    .find((ts) => ts.discount !== null)?.discount;

  const showBasketDrawer = isMinibasketVisible && basketDetails;
  const hideArrivalTimes = spaSettings?.hideArrivalTimes || false;

  return (
    <PageContext value={{ parentPageName, routingData }}>
      <SpaDayWrapper>
        <SectionContainer>
          <FormattedLink format="link" href={routingData.href}>
            <ArrowLeftIcon width="35" height="35" />
            {routingData.message}
          </FormattedLink>
          <DetailsPageHeader
            name={spaPackage.name}
            image={spaPackage.image}
            imgixParams={{ crop: 'edges' }}
            metadata={{
              guests: spaPackage.numberOfGuests,
              duration: spaPackage.durationText,
              price: spaPackage.price,
              discount,
            }}
          />
          <DetailsPageDescription title={t('spa.headingColumnTitle')}>
            <Markdown text={spaPackage.description} />
          </DetailsPageDescription>
        </SectionContainer>
        <Suggestions
          items={mergedItems}
          leadingCategoryId={
            spaPackage.crossSellTargetType === 'category' &&
            spaPackage.crossSellCategory &&
            spaPackage.crossSellCategory.id
          }
        />
        <SectionContainer>
          <SpaTermsAndConditions
            entityTermsAndConditions={spaPackage.termsAndConditions}
            spaTermsAndConditions={termsAndConditions}
          />
        </SectionContainer>
        <SpaStickyBar
          id={STICKY_BAR_ID}
          name={spaPackage.name}
          displayPriceRangeIndicator
          displayedPrice={spaPackage.price}
          numberOfGuests={spaPackage?.numberOfGuests}
          discountedPrice={discount}
          onSubmit={() =>
            router.push({
              pathname: Routes.SPA_DAY_BOOKING,
              query: {
                id: spaPackage.id,
                bookingStep: 'availability',
              },
            })
          }
        />
        {isModalOpen && (
          <SpaPackageModal
            entity={spaPackage}
            bookingStep={bookingStep}
            property={property}
            language={language}
            handleCloseModal={closeModal}
            startDate={startDate}
          />
        )}
        {showBasketDrawer && (
          <BasketDrawer
            currencyCode={basketDetails.total.currencyCode}
            open={isMinibasketVisible}
            onClose={() => setIsMinibasketVisible(false)}
            numberOfItems={calculateTotalNumberOfItemsInBasket(basketDetails.items)}
            totalCost={basketDetails.total.amount}
            title={t('basketDrawer.title')}
            items={basketDetails?.items}
            modules={modulesToRecommend}
            hideArrivalTimes={hideArrivalTimes}
            {...checkoutCta}
          />
        )}
      </SpaDayWrapper>
    </PageContext>
  );
};
